import type { IssueRelationshipCountDto } from '@issuemanager/api-client';
import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { useInfiniteIssueRelationshipCount } from '@issuemanager/client/features/issues';
import { useMemo, useState } from 'react';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type TransformedIssueRelationshipCountDto = IssueRelationshipCountDto & {
  id: string;
};

export type IssueRelationshipCountMultipleSelectorProps = MultipleSelectorProps<
  TransformedIssueRelationshipCountDto[],
  TransformedIssueRelationshipCountDto
>;

function issueRelationshipCountDataTransformer(
  data: IssueRelationshipCountDto,
) {
  return {
    ...data,
    id: String(data.relationshipCount),
  };
}

export function IssueRelationshipCountMultipleSelector(
  props: IssueRelationshipCountMultipleSelectorProps,
) {
  const { value: valueProp, onChange, enabled: _enabled, ...rest } = props;

  const value = useMemo(
    () => valueProp?.map(issueRelationshipCountDataTransformer) ?? [],
    [valueProp],
  );

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value);

  const issueRelationshipCountQuery = useInfiniteIssueRelationshipCount({
    params: {
      filter: {
        searchTerm: filterByText,
      },
    },
    enabled: filterByText.length > 0,
  });

  const options = mergePaginatedDataAndValue(
    issueRelationshipCountQuery.data?.pages.map((c) =>
      c.data.map(issueRelationshipCountDataTransformer),
    ) ?? [],
    value,
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    query: issueRelationshipCountQuery,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
  });

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={sortOptionsBySelected(
        options,
        partialValue,
        autocompleteProps.isOptionEqualToValue,
      )}
      getOptionLabel={(option) => String(option.relationshipCount)}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
